import Hls from "hls.js";
import { API_BASE } from "../core/config.js";
import { loadSession } from "../core/tokens.js";
import { onRouteLeave } from "../core/router.js";
import { h, mount } from "../core/dom.js";
import { t } from "../core/i18n.js";
import type { Cut, Label } from "../models.js";
import { api, ApiError } from "../core/api.js";

/**
 * Cut player — consumes the Worker-stitched single HLS manifest
 * GET /v1/cuts/:id/manifest.m3u8 (00 §5.4). Deepfake label shown full-video (00 §7.1).
 */
// Tracks the live HLS instance so navigating between players (or away) tears the
// previous one down instead of leaking a detached stream fetching in the background.
let activeHls: Hls | null = null;

function destroyActiveHls(): void {
  if (activeHls) {
    activeHls.destroy();
    activeHls = null;
  }
}

export async function playerView(params: Record<string, string>, root: HTMLElement): Promise<void> {
  destroyActiveHls();
  const cutId = params.cutId ?? "";
  mount(root, h("div", { class: "center muted" }, t("player.loading")));

  let cut: Cut | null = null;
  try {
    cut = await api.get<Cut>(`/v1/cuts/${cutId}`);
  } catch (e) {
    const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
    mount(root, h("div", { class: "center error" }, t("player.cutLoadFailed", { msg })));
    return;
  }

  // TODO: source the real label from the cut/variant once the API exposes it.
  const label: Label = { ai_generated: true, ai_edited: false, deepfake: false };
  const video = h("video", { controls: true, playsinline: true, "aria-label": cut?.name ?? "Cut" });
  const overlay = labelOverlay(label);
  const wrap = h(
    "div",
    { class: "player-wrap" },
    h("div", { style: "position:relative" }, video, overlay),
    h("p", { class: "muted" }, cut?.name ?? "Cut"),
    productionPanel(label),
  );
  mount(root, wrap);

  const session = loadSession();
  const auth = session ? `Bearer ${session.access_token}` : "";

  // Ask the API how to play this cut: progressive MP4 (raw R2 upload) or stitched
  // HLS (Stream-transcoded). Falls back gracefully when Stream isn't configured.
  let play: { kind: "mp4" | "hls"; url: string };
  try {
    play = await api.get<{ kind: "mp4" | "hls"; url: string }>(`/v1/cuts/${cutId}/play`);
  } catch (e) {
    const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
    overlay.after(h("p", { class: "error" }, `${t("player.manifestError")} (${msg})`));
    return;
  }

  if (play.kind === "mp4") {
    // Signed R2 URL is self-authenticating (query-signed) — play directly.
    video.src = play.url;
    return;
  }

  // HLS path (Stream): the worker-stitched manifest needs the bearer token.
  const manifestUrl = play.url || `${API_BASE}/v1/cuts/${cutId}/manifest.m3u8`;
  if (Hls.isSupported()) {
    const hls = new Hls({
      xhrSetup: (xhr) => {
        if (auth) xhr.setRequestHeader("authorization", auth);
      },
    });
    activeHls = hls;
    hls.loadSource(manifestUrl);
    hls.attachMedia(video);
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (data.fatal) overlay.after(h("p", { class: "error" }, t("player.manifestError")));
    });
    // Leaving the player route frees the stream instead of leaking a detached fetch.
    onRouteLeave(destroyActiveHls);
  } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = manifestUrl; // Safari native HLS (no custom header; needs token in URL)
  } else {
    overlay.after(h("p", { class: "error" }, t("player.hlsUnsupported")));
  }
}

function labelOverlay(label: Label): HTMLElement {
  const badges: HTMLElement[] = [];
  if (label.deepfake) badges.push(h("span", { class: "badge deepfake" }, t("compliance.deepfake")));
  else if (label.ai_generated) badges.push(h("span", { class: "badge ai" }, t("compliance.aiGenerated")));
  if (label.ai_edited) badges.push(h("span", { class: "badge ai" }, t("compliance.aiEdited")));
  return h("div", { class: "label-overlay" }, ...badges);
}

/** "제작 방식" panel — consistent AI/deepfake disclosure below the player (00 §7.1). */
function productionPanel(label: Label): HTMLElement {
  const badges: HTMLElement[] = [];
  if (label.deepfake) badges.push(h("span", { class: "badge deepfake" }, t("compliance.deepfake")));
  if (label.ai_generated) badges.push(h("span", { class: "badge ai" }, t("compliance.aiGenerated")));
  if (label.ai_edited) badges.push(h("span", { class: "badge ai" }, t("compliance.aiEdited")));

  return h(
    "div",
    { class: "card production-panel", style: "padding:14px;margin-top:14px;cursor:default" },
    h("strong", {}, t("compliance.productionPanel")),
    h(
      "div",
      { class: "badges", style: "display:flex;gap:8px;flex-wrap:wrap;margin-top:8px" },
      badges.length ? badges : h("span", { class: "muted", style: "font-size:13px" }, t("compliance.productionNone")),
    ),
    label.deepfake
      ? h("p", { class: "muted", style: "font-size:12px;margin:10px 0 0" }, t("compliance.deepfakeFullVideo"))
      : null,
  );
}
